import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CatalogService } from '../services/catalog.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { UserService } from '../../../user/services/user.service';
import { DependentService } from '../../../user/services/dependent.service';
import { RequestsService } from '../services/requests.service';
import { LegalAssistanceService } from '../services/legal-assistance.service';
import { AsistenciaRapida, Motivo } from '../models/priority-assistance.model';
import { Address, Dependent } from '../../../user/models/user-profile.model';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LegalAssistanceRequest } from '../models/legal-assistance-request.model';
import { LegalAssistanceSubserviceRequest } from '../models/legal-assistance-subservice-request.model';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-reasons-legal-assistance',
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './reasons-legal-assistance.component.html',
  styleUrl: './reasons-legal-assistance.component.scss'
})
export class ReasonsLegalAssistanceComponent implements OnInit {
  @Input() serviceType: string = '';
  motivosLegales: Motivo[] = [];
  motivoSeleccionado: string = '';
  isFuneralService: boolean = false;

  // Dependent catalog properties
  selectedPatient = 'titular';
  nameOwner: string = '';
  phoneOwner: string = '';
  keyOwner: string = '';
  addresses: Address[] = [];
  dependent: Dependent[] = [];
  userProfile: any;
  contactoDeTelefono: String = '0';
  showAllDependents = false;
  selectedName: string = '';
  email: string = '';

  private getServiceContact(): void {
    try {
      const contacts = JSON.parse(localStorage.getItem('servicesContact') || '[]');
      const clSubServicio = parseInt(this.route.snapshot.queryParams['clSubServicio']);
      const serviceContact = contacts.find((c: any) => c.clSubServicio === clSubServicio);
      this.contactoDeTelefono = serviceContact?.phone || '0';
    } catch {
      this.contactoDeTelefono = '0';
    }
  }

  constructor(
    private catalogService: CatalogService,
    private loader: LoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private dependentService: DependentService,
    private requestServ: RequestsService,
    private notificationService: NotificationService,
    private legalAssistanceService: LegalAssistanceService
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.loader.show(), 0);

    if (!this.serviceType) {
      this.serviceType = this.route.snapshot.parent?.paramMap.get('nombre') || '';
    }

    this.getServiceContact();

    console.log('Service Type:', this.serviceType);
    this.isFuneralService = this.serviceType === 'Funeraria' || this.serviceType === 'Asistencia Legal Funeraria';
    console.log('Is Funeral Service:', this.isFuneralService);

    const userData = localStorage.getItem('user');
    if (userData) {
      const userObj = JSON.parse(userData);
      this.nameOwner = userObj.name || '';
      this.phoneOwner = userObj.phone || '';
      this.keyOwner = userObj.memberships || '';
      this.email = userObj.email || '';
    }

    this.loadData();

    this.dependentService.direccionActualizada$.subscribe(() => {
      this.getDetailUser();
    });
  }

  loadData(): void {
    const cognitoId = localStorage.getItem('cognito');
    const account = localStorage.getItem('account') || '0';

    const userProfile$ = cognitoId
      ? this.userService.getUserProfile(cognitoId).pipe(catchError(() => of(null)))
      : of(null);

    const servicios$ = !this.isFuneralService
      ? this.catalogService.getServiciosRapidos(account).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin([userProfile$, servicios$]).subscribe({
      next: ([userRes, serviciosRes]) => {
        if (userRes) {
          this.userProfile = userRes;
          this.dependent = userRes.dependents;
          this.addresses = userRes.address;
        }

        if (serviciosRes?.success) {
          const asistenciaLegal = serviciosRes.asistenciaRapida.find(
            (servicio: AsistenciaRapida) => servicio.dsServicio === 'Asistencia Legal'
          );

          if (asistenciaLegal) {
            this.motivosLegales = asistenciaLegal.motivos;
            if (this.motivosLegales.length > 0) {
              this.motivoSeleccionado = this.motivosLegales[0].dsMotivo;
            }
          }
        }

        this.loader.hide();
      },
      error: () => {
        this.loader.hide();
      }
    });
  }

  seleccionarMotivo(motivo: string): void {
    this.motivoSeleccionado = this.motivoSeleccionado === motivo ? '' : motivo;
  }

  confirmarAsistencia(): void {
    if (this.isFuneralService) {
      if (!this.selectedPatient) {
        return;
      }
      this.createFuneralRequest();
    } else {
      if (!this.motivoSeleccionado) {
        return;
      }

      this.selectedName = this.selectedPatient === 'titular'
      ? this.nameOwner
      : this.selectedPatient;

      this.createLegalAutoRequest();
    }
  }

  private createLegalAutoRequest(): void {
    setTimeout(() => this.loader.show(), 0);

    const queryParams = this.route.snapshot.queryParams;
    const accountId = queryParams['clCuenta'] || localStorage.getItem('account') || 536;
    
    // Asegurar que selectedName esté correctamente asignado
    const selectedName = this.selectedPatient === 'titular' ? this.nameOwner : this.selectedPatient;
    const { firstName, lastName, secondLastName } = this.splitFullName(selectedName);
    const { firstName: ownerFirstName, lastName: ownerLastName, secondLastName: ownerSecondLastName } = this.splitFullName(this.nameOwner);

    const request: LegalAssistanceRequest = {
      identifier: {
        tenants: 'adff7f6a-e97d-11eb-9a03-0242ac130003',
        app: 'PortalAsistencia',
      },
      petition: {
        serviceId: 8,
        subServiceId: '254',
        hasAssistance: false,
        key: this.keyOwner,
        areaCode1: '044',
        phone1: this.phoneOwner,
        stateCode: '',
        municipalityCode: '',
        statusId: 0,
        accountId: accountId,
        appUserId: 1,
        countryId: 115,
        contactTypeId: 15,
        serviceTypeId: 1,
        sendCharge: false,
        hasAppointment: false,
        isRelevant: false,
        gender: 1,
        isOk: true,
        hasQuotation: false,
        isSise2: true,
        serviceReceptionId: 10,
        personId: 1,
        username: firstName,
        paternalSurname: lastName,
        userSecondLastName: secondLastName,
        contactName: selectedName,
        userFirstNamePf: ownerFirstName,
        userLastNamePf: ownerLastName,
        userSecondLastNamePf: ownerSecondLastName,
        ourUser: this.nameOwner,
        externalCabRepName: 'API CLIENTES',
        folio: crypto.randomUUID(),
        longitude: null,
        latitude: null,
        incidentDescription: this.motivoSeleccionado,
        clRecepcionServicio: 10,
        email: this.email
      },
    };

    this.legalAssistanceService.postLegalAssistance(request).pipe(
      switchMap((response) => {
        const expediente = response.response.id;

        const subRequest: LegalAssistanceSubserviceRequest = {
          identifier: {
            tenants: 'adff7f6a-e97d-11eb-9a03-0242ac130003',
            app: 'PortalAsistencia',
          },
          petition: {
            clExpediente: expediente,
            clLugarContacto: null, clDelito: null, clCulpaAjusta: null,
            clTipoDanos: null, clCausaAtlas: null, clAutoridadAtlasAl: null,
            clSiniestro: null, clTerceroApela: null,
            danosSegunAjustadorNu: '', danosSegunAjustadorCP: '',
            clAgravanteAtlas: null, declaracionSiniestro: '', observaCliente: '',
            clTipoLesionesAtlas: null, clGerenciaReg: null,
            numeroFallecidos: null, clParteAccidente: null,
            codEnt: '', codMD: '', colonia: '', calle: '', refVisuales: '',
            codEntAut: '', codMDAut: '', coloniaAut: '', nombreAut: '',
            calleAut: '', cp: '', refVisualesAut: '',
            codEntLS: '', codMDLS: '', coloniaLS: '', calleLS: '', refVisualesLS: '',
            clTipoPersona: null, ladaTelPersona: '', telefonoPersona: '',
            nombrePersona: '', telefonoAlternoPersona: '',
            fechaEnlace: null, fechaCitaFisica: null, fechaCitaTelefonica: null,
            informacionAdicional: '', motivoCita: '', obsCita: '',
          },
        };

        return this.legalAssistanceService.postLegalAssistanceSubservice(subRequest).pipe(
          switchMap(() => {
            return [response];
          })
        );
      })
    ).subscribe({
      next: (response) => {
        this.loader.hide();
        const expediente = response.response.id;

        const assistanceData = {
          clave: response.response.clave || '',
          clexpediente: expediente,
          nuestrousuario: response.response.nuestrousuario || this.nameOwner,
          clcuenta: response.response.clcuenta || '',
          nombre: 'ASISTENCIA LEGAL AUTOMOVILÍSTICA',
          clservicio: response.response.clservicio?.toString() || '8',
          dsservicio: 'Asistencia Legal',
          clsubservicio: response.response.clsubservicio?.toString() || '254',
          dssubservicio: 'Asistencia Legal Automovilística',
          clestatus: response.response.clestatus?.toString() || '0',
          dsestatus: 'Abierto',
          fecharegistro: response.response.fecharegistro || new Date().toISOString()
        };

        this.router.navigate(['/solicitud/detalle/', expediente], {
          state: assistanceData,
          queryParams: assistanceData
        });
      },
      error: (error) => {
        this.loader.hide();
        console.error('Error al solicitar asistencia legal:', error);
      },
    });
  }

  createFuneralRequest(): void {
    setTimeout(() => this.loader.show(), 0);

    const selectedName = this.selectedPatient === 'titular'
      ? this.nameOwner
      : this.selectedPatient;

    const { firstName, lastName, secondLastName } = this.splitFullName(selectedName);
    const { firstName: ownerFirstName, lastName: ownerLastName, secondLastName: ownerSecondLastName } = this.splitFullName(this.nameOwner);

    // Obtener parámetros de la URL
    const queryParams = this.route.snapshot.queryParams;
    const accountId = queryParams['clCuenta'] || localStorage.getItem('account') || 536;
    const serviceId = parseInt(queryParams['clServicio']) || (this.serviceType === 'Funeraria' ? 7 : 8);
    const subServiceId = parseInt(queryParams['clSubServicio']) || (this.serviceType === 'Funeraria' ? 292 : 298);
    const dsServicio = queryParams['dsServicio'] || (this.serviceType === 'Funeraria' ? 'Especializados' : 'Asistencia Legal');
    const dsSubservicio = queryParams['dsSubServicio'] || this.serviceType;

    const payload = {
      identifier: {
        tenants: 'adff7f6a-e97d-11eb-9a03-0242ac130003',
        app: 'PortalAsistencia'
      },
      petition: {
        serviceId: serviceId,
        subServiceId: subServiceId,
        hasAssistance: false,
        key: this.keyOwner,
        areaCode1: '044',
        phone1: this.phoneOwner,
        stateCode: '',
        municipalityCode: '',
        statusId: 0,
        accountId: accountId,
        appUserId: 1,
        countryId: 115,
        contactTypeId: 15,
        serviceTypeId: 1,
        sendCharge: false,
        hasAppointment: false,
        isRelevant: false,
        gender: 1,
        isOk: true,
        hasQuotation: false,
        isSise2: true,
        serviceReceptionId: 10,
        personId: 1,
        username: firstName,
        paternalSurname: lastName,
        userSecondLastName: secondLastName,
        contactName: this.selectedPatient === 'titular' ? this.nameOwner : this.selectedPatient,
        userFirstNamePf: ownerFirstName,
        userLastNamePf: ownerLastName,
        userSecondLastNamePf: ownerSecondLastName,
        ourUser: this.nameOwner,
        externalCabRepName: 'API CLIENTES',
        folio: crypto.randomUUID(),
        longitude: '',
        latitude: '',
        incidentDescription: '',
        emial: this.email,
      }
    };

    const serviceMethod = this.serviceType === 'Funeraria'
      ? this.requestServ.sendFunerariaRequest(payload)
      : this.requestServ.sendLegalFunerariaRequest(payload);

    serviceMethod.subscribe({
      next: (res) => {
        this.loader.hide();
        console.log('Solicitud enviada correctamente', res);

        const expediente = res?.response?.id || res?.data?.clExpediente || res?.clExpediente;

        const assistanceData = {
          clave: res.response.clave || '',
          clexpediente: expediente,
          nuestrousuario: res.response.nuestrousuario || this.nameOwner,
          clcuenta: res.response.clcuenta || '',
          nombre: this.serviceType.toUpperCase(),
          clservicio: res.response.clservicio || serviceId.toString(),
          dsservicio: dsServicio,
          clsubservicio: res.response.clsubservicio || subServiceId.toString(),
          dssubservicio: dsSubservicio,
          clestatus: res.response.clestatus || '0',
          dsestatus: 'Abierto',
          fecharegistro: res.response.fecharegistro || new Date().toISOString()
        };

        this.router.navigate(['/solicitud/detalle/', expediente], {
          state: assistanceData,
          queryParams: assistanceData
        }).then(() => {
          // Mostrar mensaje de condolencias para servicios funerarios sin click to call
          if (this.notificationService.isFuneralService(this.serviceType)) {
            setTimeout(() => {
              this.notificationService.showCondolences();
            }, 500);
          }
        });
      },
      error: (err) => {
        this.loader.hide();
        console.error('Error al enviar solicitud', err);
      }
    });
  }

  private splitFullName(fullName: string) {
    const parts = fullName.trim().split(/\s+/).filter(p => p);
    if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '', secondLastName: '' };
    if (parts.length === 2) return { firstName: parts[0], lastName: parts[1], secondLastName: '' };
    if (parts.length === 3) return { firstName: parts[0], lastName: parts[1], secondLastName: parts[2] };
    // Para nombres con 4 o más partes, asumir que los primeros 2 son nombres y los últimos 2 son apellidos
    return {
      firstName: parts.slice(0, 2).join(' '),
      lastName: parts[parts.length - 2],
      secondLastName: parts[parts.length - 1]
    };
  }

  goToCall() {
    window.open(`tel:${this.contactoDeTelefono}`, '_self');
  }

  // Dependent catalog methods
  getDetailUser(): void {
    const cognitoId = localStorage.getItem('cognito');
    if (cognitoId) {
      this.userService.getUserProfile(cognitoId).subscribe({
        next: (res) => {
          this.userProfile = res;
          this.dependent = res.dependents;
          this.addresses = res.address;
        },
        error: (err) => console.error('Error al obtener perfil', err)
      });
    }
  }

  isSelected(value: string): boolean {
    return this.selectedPatient === value;
  }

  addNewDependent(): void {
    this.router.navigate(['add-dependiente'], { queryParams: { returnUrl: this.router.url } });
  }

  toggleDependents() {
    this.showAllDependents = !this.showAllDependents;
  }
}
